/**
 * Notifications inbox.
 *
 * The bell button polls every 60s for items operators care about:
 *   • Unresolved critical/high anomalies
 *   • Platform bugs in `new` status
 *   • SLA-breached customer tickets (priority/last_activity heuristic)
 *
 * Click → slide-in Drawer listing the items grouped by source.
 *
 * This is purely a read aggregator — no backend "notifications" table.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Bug, AlertTriangle, Ticket, ArrowRight, ShieldCheck,
} from 'lucide-react';
import {
  AdminApiClient,
  type AnomalyRow, type PlatformBugRow,
  type CustomerTicketRow, type TicketPriority,
} from '../../api';
import { useSession } from '../../session';
import { Drawer } from '../ui/Drawer';
import { failureFrom } from '../../lib/api-state';
import { StatusChip, type Band } from '../ui/StatusChip';

interface NotificationItem {
  id: string;
  source: 'anomaly' | 'bug' | 'ticket';
  title: string;
  hint: string;
  band: Band;
  href: string;
  at: string;
}

const SLA_TARGETS_H: Record<TicketPriority, number> = {
  urgent: 1, high: 4, normal: 24, low: 72,
};

export function NotificationsBell(): JSX.Element {
  const { state } = useSession();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [anomalies, setAnomalies] = useState<AnomalyRow[]>([]);
  const [bugs, setBugs] = useState<PlatformBugRow[]>([]);
  const [tickets, setTickets] = useState<CustomerTicketRow[]>([]);
  const [tick, setTick] = useState(0);
  // Sources that could not be checked. A failed check is "unknown", never "all quiet".
  const [failed, setFailed] = useState<string[]>([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!state.token) return undefined;
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    const bad: string[] = [];
    const note = (label: string) => (e: unknown): void => { bad.push(label); failureFrom(e, label); };
    void Promise.all([
      api.anomalies(true).then((r) => setAnomalies(r.anomalies)).catch(note('anomalies')),
      api.platformBugs().then((r) => setBugs(r.platform_bugs)).catch(note('engine bugs')),
      api.customerTickets('open_or_awaiting').then((r) => setTickets(r.tickets)).catch(note('tickets')),
    ]).then(() => { setFailed(bad); setChecked(true); });
    const id = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(id);
  }, [state.token, tick]);

  const items = useMemo<NotificationItem[]>(() => {
    const out: NotificationItem[] = [];

    // Anomalies (critical / high only).
    for (const a of anomalies) {
      const sev = a.severity.toLowerCase();
      if (sev !== 'critical' && sev !== 'high') continue;
      out.push({
        id: `anom-${a.id}`,
        source: 'anomaly',
        title: a.message,
        hint: `${a.code} · ${a.severity}`,
        band: sev === 'critical' ? 'critical' : 'warning',
        href: '/anomalies',
        at: a.detected_at,
      });
    }

    // New platform bugs.
    for (const b of bugs) {
      if (b.status !== 'new') continue;
      out.push({
        id: `bug-${b.id}`,
        source: 'bug',
        title: b.summary,
        hint: `${b.binary_version} · ${b.occurrences} hits`,
        band: 'critical',
        href: '/platform-bugs',
        at: b.last_seen_at,
      });
    }

    // SLA-breached tickets.
    const now = Date.now();
    for (const t of tickets) {
      if (t.status !== 'open' && t.status !== 'awaiting_staff') continue;
      const targetH = SLA_TARGETS_H[t.priority] ?? 24;
      const ageH = (now - new Date(t.last_activity_at).getTime()) / 1000 / 3600;
      if (ageH <= targetH) continue;
      out.push({
        id: `ticket-${t.id}`,
        source: 'ticket',
        title: t.subject,
        hint: `${t.priority} · ${(ageH - targetH).toFixed(1)}h over SLA`,
        band: 'critical',
        href: '/tickets',
        at: t.last_activity_at,
      });
    }

    out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    return out;
  }, [anomalies, bugs, tickets]);

  const groups = useMemo(() => {
    return {
      anomaly: items.filter((i) => i.source === 'anomaly'),
      bug:     items.filter((i) => i.source === 'bug'),
      ticket:  items.filter((i) => i.source === 'ticket'),
    };
  }, [items]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Notifications · ${items.length} pending`}
        title={items.length ? `${items.length} pending notifications` : failed.length ? 'Some notifications could not be checked' : 'No new notifications'}
        style={{
          position: 'relative',
          width: 36, height: 36,
          borderRadius: 10,
          border: '1px solid var(--border)',
          background: 'var(--bg-overlay)',
          color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          flex: 'none',
        }}
      >
        <Bell width={16} height={16} />
        {items.length > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4, right: -4,
              minWidth: 18, height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: 'var(--danger)',
              color: '#0b0f19',
              fontSize: '0.625rem',
              fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid var(--bg)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {items.length > 99 ? '99+' : items.length}
          </span>
        )}
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="md"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Bell width={14} height={14} />
            Notifications
          </span>
        }
        subtitle={
          items.length > 0
            ? `${items.length} item${items.length === 1 ? '' : 's'} need attention`
            : failed.length > 0 ? 'Could not check everything' : checked ? 'Inbox is clear' : 'Checking…'
        }
      >
        {failed.length > 0 && (
          <p role="alert" style={{
            margin: '0 0 12px', padding: '8px 12px', borderRadius: 10,
            border: '1px solid var(--border)', background: 'var(--bg-overlay)',
            fontSize: '0.8125rem', color: 'var(--text-muted)',
          }}>
            Could not check {failed.join(', ')} right now, so this list may be incomplete.{' '}
            <button type="button" onClick={() => setTick((v) => v + 1)}>Retry</button>
          </p>
        )}
        {items.length === 0 && (failed.length > 0 || !checked) ? null : items.length === 0 ? (
          <div
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: 32,
              gap: 8,
              color: 'var(--text-muted)',
              borderRadius: 12,
              border: '1px dashed var(--border)',
              background: 'var(--bg-overlay)',
            }}
          >
            <ShieldCheck width={28} height={28} color="var(--success)" />
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              All quiet
            </p>
            <p style={{ margin: 0, fontSize: '0.75rem', textAlign: 'center', maxWidth: 320 }}>
              No critical/high anomalies, new engine bugs, or breached tickets right now.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Group
              icon={AlertTriangle}
              label="Active anomalies"
              tone="#f87171"
              items={groups.anomaly}
              onPick={(href) => { setOpen(false); nav(href); }}
            />
            <Group
              icon={Bug}
              label="New platform bugs"
              tone="#fb923c"
              items={groups.bug}
              onPick={(href) => { setOpen(false); nav(href); }}
            />
            <Group
              icon={Ticket}
              label="Breached tickets"
              tone="#22d3ee"
              items={groups.ticket}
              onPick={(href) => { setOpen(false); nav(href); }}
            />
          </div>
        )}
      </Drawer>
    </>
  );
}

function Group({
  icon: Icon, label, tone, items, onPick,
}: {
  icon: typeof Bug;
  label: string;
  tone: string;
  items: NotificationItem[];
  onPick: (href: string) => void;
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 8,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        <Icon width={12} height={12} color={tone} />
        {label}
        <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
          {items.length}
        </span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.slice(0, 8).map((i) => (
          <li key={i.id}>
            <button
              type="button"
              onClick={() => onPick(i.href)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                aria-hidden
                style={{
                  flex: 'none',
                  width: 4, alignSelf: 'stretch',
                  borderRadius: 2,
                  background: tone,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '0.8125rem', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {i.title}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                  <StatusChip band={i.band} size="sm" noDot>
                    {i.hint}
                  </StatusChip>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                    {timeAgo(i.at)}
                  </span>
                </div>
              </div>
              <ArrowRight width={12} height={12} color="var(--text-muted)" />
            </button>
          </li>
        ))}
        {items.length > 8 && (
          <li
            style={{
              fontSize: '0.6875rem', color: 'var(--text-muted)',
              padding: '4px 12px',
            }}
          >
            +{items.length - 8} more — open the source page for the full list.
          </li>
        )}
      </ul>
    </section>
  );
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
